
































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import notificationService from '@/services/notificationService';
import GenerateInvoiceFromProgressStatementsDialog from '@/modules/invoicing/components/GenerateInvoiceFromProgressStatementsDialog.vue';

import Datagrid from '@/components/default/Datagrid.vue';
import Card from '@/components/default/Card.vue';
import { ProgressStatementListItemDto, ProgressStatementStatus, ProgressStatementsClient } from '@/data/web-api-client';

@Component({
  components: {
    Datagrid,
    Card,
    GenerateInvoiceFromProgressStatementsDialog,
  },
})
export default class ProgressStatementsTable extends Vue {
  public progressStatements: ProgressStatementListItemDto[] = [];

  @Prop({ required: true })
  projectId: any;

  @Prop({ required: false, default: false })
  public disabled!: boolean;

  loading: boolean = true;
  creating: boolean = false;
  progressStatementsClient: ProgressStatementsClient = new ProgressStatementsClient(undefined, this.$axios);

  public refresh() {
    this.loadData();
  }

  created() {
    this.loadData();
  }

  openDetails(e: any) {
    if (e.row && !e.row.isEditing && e.column && e.column.type !== 'buttons' && e.column.type !== 'selection') {
      const documentNumber = `${e.row.data.documentNumber}-${e.row.data.versionNumber}`.replaceAll('/', '-');

      this.$router.push({ path: `/progressStatements/${documentNumber}` });
    }
  }

  deleteRow(e: any) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        const documentNumber = `${e.data.documentNumber}-${e.data.versionNumber}`.replaceAll('/', '-');

        await self.progressStatementsClient.delete(documentNumber);
        notificationService.saved();

        resolve(false);
      } catch (err) {
        notificationService.showErrorMessage(err);
        resolve(true);
      }
    });
  }

  async loadData() {
    try {
      this.loading = true;
      this.progressStatements = await this.progressStatementsClient.getFromProject(this.projectId);
    } catch {
      notificationService.failedToLoad();
    } finally {
      this.loading = false;
    }
  }

  async createProgressStatement() {
    try {
      this.creating = true;
      const documentNumber = await this.progressStatementsClient.create(this.projectId);
      this.$router.push({ path: `/ProgressStatements/${documentNumber.replaceAll('/', '-')}` });
    } catch (err) {
      notificationService.showAxiosError(err);
    } finally {
      this.creating = false;
    }
  }

  async generateInvoice() {
    const grid = this.$refs.grid as Datagrid;
    if (grid) {
      const selectedRows: ProgressStatementListItemDto[] = (grid.$refs.ProgressStatementsTable as any).instance.getSelectedRowsData();
      const idList = selectedRows.map((o) => `${o.documentNumber}-${o.versionNumber}`);
      try {
        (this.$refs.GenerateInvoiceFromProgressStatementsDialog as GenerateInvoiceFromProgressStatementsDialog).open(idList as string[]);
      } catch (err) {
        notificationService.showAxiosError(err);
      }
    }
  }

  get columns() {
    return [
      {
        dataField: 'documentNumber',
        caption: 'Documentnr.',
        calculateCellValue(value: any) {
          return `${value.documentNumber}-${value.versionNumber}`;
        },
      },
      {
        dataField: 'customer.displayName',
        caption: 'Klant',
      },
      {
        dataField: 'contact.displayName',
        caption: 'Contact',
      },
      {
        dataField: 'date',
        caption: 'Datum',
        dataType: 'date',
        format: 'dd/MM/yyyy',
      },
      {
        dataField: 'status',
        caption: this.$t('objects.progressStatements.fields.Status'),
        lookup: {
          dataSource: [
            { value: ProgressStatementStatus.Draft, text: 'Klad' },
            { value: ProgressStatementStatus.Open, text: 'Open' },
            { value: ProgressStatementStatus.Send, text: 'Verstuurd' },
            { value: ProgressStatementStatus.Approved, text: 'Aanvaard' },
            { value: ProgressStatementStatus.Declined, text: 'Afgewezen' },
            { value: ProgressStatementStatus.Replaced, text: 'Vervangen' },
            { value: ProgressStatementStatus.Invoiced, text: 'Gefactureerd' },
          ],
          displayExpr: 'text',
          valueExpr: 'value',
        },
      },
      {
        caption: 'Totalen',
        columns: [
          {
            dataField: 'totalCost',
            caption: 'Kost',
            format: {
              type: 'currency',
              precision: 2,
            },
            alignment: 'left',
          },
          {
            dataField: 'totalMargin',
            caption: 'Marge',
            format: {
              type: 'currency',
              precision: 2,
            },
            alignment: 'left',
          },
          {
            dataField: 'total',
            caption: 'Totaal',
            format: {
              type: 'currency',
              precision: 2,
            },
            alignment: 'left',
          },
        ],
      },
      {
        type: 'buttons',
        buttons: ['delete'],
      },
    ];
  }

  @Watch('projectId')
  onPropertyChanged(value: boolean, oldValue: boolean) {
    this.loadData();
  }
}
